import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    TextField,
    Button,
    IconButton,
    Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { addPartner, deletePartner } from '../../../../store/startup/company/companySlice';
import { fetchPartners } from '../../../../store/startup/company/companySlice';

const PartnerAccordion = ({ partner, startupId, onSaved }) => {
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(partner.isNew || false);
    const [errors, setErrors] = useState({
        name: false,
        title: false,
        education: false,
        shareAmount: false,
        shareRatio: false,
        voteRatio: false,
        privilege: false,
        relation: false,
        experience: false,
        proficiency: false
    });
    const [formData, setFormData] = useState({
        name: partner.name || '',
        title: partner.title || '',
        education: partner.education || '',
        gpa: partner.gpa || '',
        cvPath: partner.cvPath || '',
        countryCode: partner.countryCode || 'TR',
        shareAmount: partner.shareAmount || '',
        shareRatio: partner.shareRatio || '',
        voteRatio: partner.voteRatio || '',
        privilege: partner.privilege || '',
        relation: partner.relation || '',
        experience: partner.experience || '',
        proficiency: partner.proficiency || ''
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        // Clear error when user starts typing
        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        // Reset errors
        setErrors({
            name: false,
            title: false,
            education: false,
            shareAmount: false,
            shareRatio: false,
            voteRatio: false,
            privilege: false,
            relation: false,
            experience: false,
            proficiency: false
        });

        // Validate inputs
        const newErrors = {
            name: !formData.name.trim(),
            title: !formData.title.trim(),
            education: !formData.education.trim(),
            shareAmount: !formData.shareAmount.toString().trim(),
            shareRatio: !formData.shareRatio.toString().trim(),
            voteRatio: !formData.voteRatio.toString().trim(),
            privilege: !formData.privilege.trim(),
            relation: !formData.relation.trim(),
            experience: !formData.experience.trim(),
            proficiency: !formData.proficiency.trim()
        };

        // Check if any errors exist
        if (Object.values(newErrors).some(error => error)) {
            setErrors(newErrors);
            return;
        }

        try {
            await dispatch(addPartner({
                startupId,
                ...formData
            })).unwrap();
            if (onSaved) onSaved();
            setExpanded(false);
        } catch (error) {
            console.error('Failed to save partner:', error);
        }
    };

    const handleDelete = async (e) => {
        e.stopPropagation(); // Prevent accordion from toggling
        try {
            await dispatch(deletePartner(
                partner.startupPartnerId
            )).unwrap();
            // Refresh the partners list after deletion
            dispatch(fetchPartners(startupId));
        } catch (error) {
            console.error('Failed to delete partner:', error);
        }
    };

    return (
        <Accordion 
            expanded={expanded} 
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 2 }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>
                        {formData.name || 'Yeni Partner'}
                    </Typography>
                    {!partner.isNew && (
                        <IconButton 
                            onClick={handleDelete}
                            size="small"
                            sx={{ mr: 2 }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="İsim / Ünvan"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            error={errors.name}
                            helperText={errors.name ? "İsim alanı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Ünvan"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            error={errors.title}
                            helperText={errors.title ? "Ünvan alanı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Eğitim"
                            name="education"
                            value={formData.education}
                            onChange={handleChange}
                            
                            
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Not Ortalaması"
                            name="gpa"
                            type="number"
                            value={formData.gpa}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Pay Tutarı"
                            name="shareAmount"
                            type="number"
                            value={formData.shareAmount}
                            onChange={handleChange}
                            error={errors.shareAmount}
                            helperText={errors.shareAmount ? "Pay adedi zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Pay Oranı (%)"
                            name="shareRatio"
                            type="number"
                            value={formData.shareRatio}
                            onChange={handleChange}
                            error={errors.shareRatio}
                            helperText={errors.shareRatio ? "Pay oranı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Oy Hakkı (%)"
                            name="voteRatio"
                            type="number"
                            value={formData.voteRatio}
                            onChange={handleChange}
                            error={errors.voteRatio}
                            helperText={errors.voteRatio ? "Oy hakkı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="İmtiyazlar"
                            name="privilege"
                            multiline
                            rows={2}
                            value={formData.privilege}
                            onChange={handleChange}
                            error={errors.privilege}
                            helperText={errors.privilege ? "İmtiyazlar alanı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="İlişki"
                            name="relation"
                            value={formData.relation}
                            onChange={handleChange}
                            error={errors.relation}
                            helperText={errors.relation ? "İlişki alanı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Deneyim"
                            name="experience"
                            multiline
                            rows={3}
                            value={formData.experience}
                            onChange={handleChange}
                            error={errors.experience}
                            helperText={errors.experience ? "Deneyim alanı zorunludur" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Yetkinlikler"
                            name="proficiency"
                            multiline
                            rows={3}
                            value={formData.proficiency}
                            onChange={handleChange}
                            error={errors.proficiency}
                            helperText={errors.proficiency ? "Yetkinlikler alanı zorunludur" : ""}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                    >
                        Kaydet
                    </Button>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default PartnerAccordion;
